<template>
  <b-card class="card-box card__badge-card" body-class="py-3">
    <div :id="downloadableContainerId" class="certificate-container w-0 h-0">
      <BadgeDetailCard
        v-if="isDataReady"
        class="badge-detail-card"
        :badge-info="badgeInfo"
        :is-downloadable="true"
        @ready="downloadBadge"
      ></BadgeDetailCard>
    </div>
    <b-row style="min-height: 383.84px" class="align-content-between">
      <b-col cols="12" class="p-0">
        <b-col cols="12" class="text-center">
          <badge-image :href="badgeHref" :src="badgeImageSrc" centered></badge-image>
        </b-col>
        <b-col
          v-if="learningUnit && learningUnit.level_type"
          class="text-primary font-weight-bold text-center mt-4"
        >
          <span class="mr-1">
            <img :src="levelTypeIcon" style="height: 16px" />
          </span>
          <span class="text__mc-type">{{ levelTypeLabel }}</span>
        </b-col>
        <b-col
          v-if="learningUnit"
          class="h5 font-weight-bold mt-3 text__mc-title text-center card-title__truncate-overflow"
          cols="12"
        >
          {{ learningUnit.name }}
        </b-col>
      </b-col>

      <b-col class="p-0">
        <b-col cols="12" class="text-center">
          <div class="text-black-50 mb-3">Issued by</div>
          <img src="@/assets/kmutt-logo.png" width="30.2px" />
        </b-col>

        <b-col cols="12">
          <hr class="my-4" />
        </b-col>

        <b-col cols="12">
          <div
            class="d-flex flex-row justify-content-between align-items-baseline footer__badge-card"
          >
            <div class="mb-3">
              <span class="text-black-50">Issued on </span>
              <a target="_blank" :href="credential.accredible_credential.url">
                {{ formatedIssuedDate }}
              </a>
            </div>
            <div class="d-flex flex-row justify-content-between buttons__badge-card">
              <b-button
                variant="outline-primary"
                class="px-3 py-1 button_badge-share"
                @click="show = true"
              >
                <font-awesome-icon icon="share-nodes" size="lg" />
              </b-button>

              <b-button
                variant="outline-primary"
                class="px-3 py-1 button_badge-download"
                @click="handleDownload"
              >
                <b-img v-if="!isDownloading" width="20px" :src="downLoadSvg" />
                <b-spinner v-else small />
              </b-button>
            </div>
          </div>
        </b-col>
      </b-col>
    </b-row>

    <share-badge-modal
      v-if="show"
      :key="credential.id"
      :show.sync="show"
      :credential-id="credential.id"
    ></share-badge-modal>
  </b-card>
</template>

<script>
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import Credential from '@/models/Credential';
import ShareBadgeModal from '@/views/Badge/components/ShareBadgeModal.vue';
import BadgeImage from '@/components/Credential/BadgeImage.vue';
import downloadSvg from '@/assets/icon-files-list/download.svg';
import html2canvas from 'html2canvas';
import BadgeDetailCard from '@/views/Badge/components/BadgeDetailCard.vue';
import specificIcon from '@/assets/learning-unit-types/specific.svg';
import academicIcon from '@/assets/learning-unit-types/academic.svg';
import Utility from '@/models/Utility';

export default {
  components: {
    BadgeImage,
    BadgeDetailCard,
    ShareBadgeModal
  },

  mixins: [datetimeMixin, userInfoMixin],

  props: {
    credential: {
      type: Credential,
      required: true
    }
  },

  data() {
    return {
      show: false,
      downLoadSvg: downloadSvg,
      isDownloading: false,
      isDataReady: false,
      badgeInfo: {}
    };
  },

  computed: {
    learningUnit() {
      return this.credential.learning_unit || null;
    },

    downloadableContainerId() {
      return `downloadable-container-${this.learningUnit?.id}`;
    },

    levelTypeIcon() {
      return this.learningUnit?.level_type === 'CAREER_SPECIFIC' ? specificIcon : academicIcon;
    },

    levelTypeLabel() {
      return this.learningUnit?.level_type === 'CAREER_SPECIFIC'
        ? 'Career Specific'
        : 'Academic Focused';
    },

    badgeHref() {
      return this.credential.accredible_credential?.url || '';
    },

    badgeImageSrc() {
      return this.learningUnit?.badge_image?.[0]?.url || '';
    },

    formatedIssuedDate() {
      return this.formatedDatetime(this.credential.accredible_credential.issued_on, 'MMM D, YYYY');
    }
  },
  methods: {
    async handleDownload() {
      if (!this.learningUnit?.id) return;

      this.isDownloading = true;
      await this.fetchBadgeDetail(this.learningUnit.id);
    },
    async fetchBadgeDetail(learningUnitId) {
      try {
        const {
          response: { data = {} }
        } = await Credential.api().fetchCredentialDetail(learningUnitId, this.userId);

        this.badgeInfo = {
          ...data,
          issued_to: data.accredible_credential.recipient.name,
          issued_on: this.formatedDatetime(data.accredible_credential.issued_on, 'MMM D, YYYY')
        };
        this.isDataReady = true;
      } catch (error) {
        console.error('Error fetching badge detail:', error);
        this.isDownloading = false;
      }
    },
    resizeCanvas(canvas, newWidth, newHeight) {
      const context = canvas.getContext('2d');

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = newWidth;
      tempCanvas.height = newHeight;
      const tempContext = tempCanvas.getContext('2d');

      tempContext.drawImage(canvas, 0, 0, newWidth, newHeight);

      canvas.width = newWidth;
      canvas.height = newHeight;

      context.drawImage(tempCanvas, 0, 0);
    },
    async downloadBadge() {
      try {
        if (this.badgeInfo.learning_unit.level_type === 'CAREER_SPECIFIC') {
          await this.downloadImage();
        } else {
          await this.downloadCert(this.badgeInfo.accredible_credential.certificate.image.preview);
        }
      } catch (error) {
        console.error('Error downloading badge:', error);
      } finally {
        this.resetDownloadState();
      }
    },
    resetDownloadState() {
      this.isDownloading = false;
      this.badgeInfo = {};
      this.isDataReady = false;
    },
    async downloadCert(imageUrl) {
      try {
        const { response } = await Utility.api().proxyImage(imageUrl);
        const blob = new Blob([response.data], { type: response.data.type });
        const blobUrl = window.URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.setAttribute('download', `${this.learningUnit.name}-certificate.png`);
        a.setAttribute('href', blobUrl);
        a.click();

        // Clean up the blob URL after download starts
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading certificate:', error);
      }
    },
    async downloadImage() {
      try {
        const node = document.getElementById(`downloadable-container-${this.learningUnit.id}`);

        const canvas = await html2canvas(node, {
          onclone: async (ele) => {
            const content = ele.getElementById(`downloadable-container-${this.learningUnit.id}`);
            content.style.opacity = 1;
            content.style.minWidth = '1040px';
            content.style.maxHeight = '512px';
            return ele;
          },
          allowTaint: true,
          useCORS: true,
          backgroundColor: null
        });

        const image = new Image();
        image.src = canvas.toDataURL('image/png');

        await new Promise((resolve) => {
          image.onload = resolve;
        });

        this.resizeCanvas(canvas, 1040, 512);
        const imageUrl = canvas.toDataURL('image/png', 1.0);

        const a = document.createElement('a');
        a.setAttribute('download', `${this.learningUnit.name}-badge.png`);
        a.setAttribute('href', imageUrl);
        a.click();
      } catch (error) {
        console.error('Error generating badge image:', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.card__badge-card {
  min-height: 383.84px;
  max-height: 439.84px;
  width: 100%;
  height: auto;
}

.card-title__truncate-overflow {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.footer__badge-card {
  gap: 8px;

  .buttons__badge-card {
    gap: 8px;
  }
}

.button_badge-download:hover {
  img {
    content: url('../../assets/icon-files-list/download-white.svg');
  }
}
.certificate-container {
  background-color: #ffffff;
  width: 330px;
  height: 512px;
  display: flex;

  position: absolute;

  opacity: 0;
  padding: 20px 0;
  top: 0px;
  left: 0px;
  z-index: -1;
}
</style>
